import { useMemo } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceDot,
} from "recharts";
import { cn } from "@/lib/utils";

interface PaymentCurveChartProps {
  currentScore: number;
  lowScenarioInput: number;
  medicareRevenue: number;
  scalingFactor?: number;
  className?: string;
}

// Payment-adjustment math lives in the shared lib (R10 extract 2026-08-03)
import { computeAdjustmentPct as calculateAdjustment } from '@/lib/payment-adjustment';

function formatCurrency(amount: number): string {
  const absAmount = Math.abs(amount);
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(absAmount);
  
  if (amount < 0) return '-' + formatted;
  if (amount > 0) return '+' + formatted;
  return formatted;
}

export function PaymentCurveChart({
  currentScore,
  lowScenarioInput,
  medicareRevenue,
  scalingFactor = 0.74,
  className
}: PaymentCurveChartProps) {
  // Generate data points for the chart
  const chartData = useMemo(() => {
    const data = [];
    for (let score = 0; score <= 100; score += 1) {
      const adjustmentPercent = calculateAdjustment(score, lowScenarioInput, scalingFactor);
      const dollarAmount = medicareRevenue * adjustmentPercent;
      data.push({
        score,
        adjustmentPercent: adjustmentPercent * 100, // Convert to percentage
        dollarAmount,
        isPenalty: adjustmentPercent < 0,
        isBonus: adjustmentPercent > 0,
      });
    }
    return data;
  }, [lowScenarioInput, medicareRevenue, scalingFactor]);

  // Current score data point
  const currentAdjustment = calculateAdjustment(currentScore, lowScenarioInput, scalingFactor);
  const currentDollarAmount = medicareRevenue * currentAdjustment;

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-popover border rounded-lg p-3 shadow-lg">
          <p className="font-medium">Score: {label}</p>
          <p className={cn(
            "text-sm",
            data.isPenalty ? "text-red-600" : data.isBonus ? "text-emerald-600" : "text-muted-foreground"
          )}>
            {data.adjustmentPercent.toFixed(2)}%
          </p>
          <p className={cn(
            "text-sm font-medium",
            data.isPenalty ? "text-red-600" : data.isBonus ? "text-emerald-600" : "text-muted-foreground"
          )}>
            {formatCurrency(data.dollarAmount)}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={cn("w-full", className)}>
      <div className="h-[280px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          >
            <defs>
              <linearGradient id="penaltyGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="bonusGradient" x1="0" y1="1" x2="0" y2="0">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis 
              dataKey="score" 
              tick={{ fontSize: 12 }}
              tickLine={false}
              axisLine={{ className: "stroke-muted" }}
              ticks={[0, 18, 50, 75, 100]}
            />
            <YAxis 
              tickFormatter={(value) => `${value.toFixed(1)}%`}
              tick={{ fontSize: 12 }}
              tickLine={false}
              axisLine={{ className: "stroke-muted" }}
              domain={['auto', 'auto']}
            />
            <Tooltip content={<CustomTooltip />} />
            
            {/* Zero reference line */}
            <ReferenceLine y={0} stroke="#888" strokeDasharray="5 5" />
            
            {/* Neutral point marker */}
            <ReferenceLine x={75} stroke="#888" strokeDasharray="3 3" />
            
            {/* Area fill */}
            <Area
              type="monotone"
              dataKey="adjustmentPercent"
              stroke="#888"
              strokeWidth={2}
              fill="url(#penaltyGradient)"
              fillOpacity={1}
            />
            
            {/* Current score marker */}
            <ReferenceDot
              x={Math.round(currentScore)}
              y={currentAdjustment * 100}
              r={8}
              fill={currentAdjustment < 0 ? "#ef4444" : currentAdjustment > 0 ? "#10b981" : "#888"}
              stroke="#fff"
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      
      {/* Current Position Callout */}
      <div className="flex justify-center mt-2">
        <div className={cn(
          "px-4 py-2 rounded-lg text-sm",
          currentAdjustment < 0 ? "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300" :
          currentAdjustment > 0 ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300" :
          "bg-muted text-muted-foreground"
        )}>
          <span className="font-medium">Your Score ({Math.round(currentScore)}): </span>
          <span>{(currentAdjustment * 100).toFixed(2)}% = {formatCurrency(currentDollarAmount)}</span>
        </div>
      </div>
    </div>
  );
}

